import { useState, useEffect, useCallback } from 'react';
import { useFamilyContext } from '../contexts/FamilyContext';
import { tasksService } from '../lib/tasks-service';
import { messagesService } from '../lib/messages-service';
import { shoppingService } from '../lib/shopping-service';
import { collegeJourneyProgressService } from '../lib/college-journey-service';

export function useSidebarBadges() {
  const { family, currentUser } = useFamilyContext();

  const [badges, setBadges] = useState({
    tasks: 0,
    shopping: 0,
    messages: 0,
    college: 0,
    recommendations: 0,
    events: 0,
    allowance: 0,
    packing: 0
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Count pending and overdue tasks
  const getTasksCount = useCallback(async () => {
    if (!family?.id || !currentUser?.id) return 0;

    try {
      // Get pending tasks
      const pendingTasks = await tasksService.getTasks(currentUser.id, {
        status: 'pending'
      });

      // Get overdue tasks
      const overdueTasks = await tasksService.getTasks(currentUser.id, {
        overdue: true
      });

      // Combine and deduplicate (in case a task is both pending and overdue)
      const allTaskIds = new Set([
        ...pendingTasks.map(t => t.id),
        ...overdueTasks.map(t => t.id)
      ]);

      return allTaskIds.size;
    } catch (error) {
      console.error('Error getting tasks count:', error);
      return 0;
    }
  }, [family?.id, currentUser?.id]);

  // Count pending shopping items
  const getShoppingCount = useCallback(async () => {
    if (!family?.id) return 0;

    try {
      const items = await shoppingService.getItems({
        familyId: family.id,
        status: 'pending'
      });

      return items?.length || 0;
    } catch (error) {
      console.error('Error getting shopping count:', error);
      return 0;
    }
  }, [family?.id]);

  // Count unread messages
  const getMessagesCount = useCallback(async () => {
    if (!family?.id || !currentUser?.id) return 0;

    try {
      const count = await messagesService.getUnreadCount(currentUser.id);
      return count || 0;
    } catch (error) {
      console.error('Error getting messages count:', error);
      return 0;
    }
  }, [family?.id, currentUser?.id]);

  // Count overdue college items
  const getCollegeCount = useCallback(async () => {
    if (!currentUser?.id) return 0;

    try {
      const progress = await collegeJourneyProgressService.getProgress(currentUser.id);

      // For now, let's show a badge if college journey is incomplete
      // This can be enhanced later with actual overdue milestones
      if (!progress || !progress.onboarding_complete) {
        return 1; // Show badge for incomplete onboarding
      }

      return 0;
    } catch (error) {
      console.error('Error getting college count:', error);
      return 0;
    }
  }, [currentUser?.id]);

  // Placeholder functions for other badges
  const getRecommendationsCount = useCallback(async () => 0, []);
  const getEventsCount = useCallback(async () => 0, []);
  const getAllowanceCount = useCallback(async () => 0, []);
  const getPackingCount = useCallback(async () => 0, []);

  // Load all badge counts
  const loadBadgeCounts = useCallback(async () => {
    if (!family?.id || !currentUser?.id) return;

    setLoading(true);
    setError(null);

    try {
      const [
        tasksCount,
        shoppingCount,
        messagesCount,
        collegeCount,
        recommendationsCount,
        eventsCount,
        allowanceCount,
        packingCount
      ] = await Promise.all([
        getTasksCount(),
        getShoppingCount(),
        getMessagesCount(),
        getCollegeCount(),
        getRecommendationsCount(),
        getEventsCount(),
        getAllowanceCount(),
        getPackingCount()
      ]);

      setBadges({
        tasks: tasksCount,
        shopping: shoppingCount,
        messages: messagesCount,
        college: collegeCount,
        recommendations: recommendationsCount,
        events: eventsCount,
        allowance: allowanceCount,
        packing: packingCount
      });
    } catch (err) {
      console.error('Error loading badge counts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [
    family?.id,
    currentUser?.id,
    getTasksCount,
    getShoppingCount,
    getMessagesCount,
    getCollegeCount,
    getRecommendationsCount,
    getEventsCount,
    getAllowanceCount,
    getPackingCount
  ]);

  // Load badge counts on mount and when dependencies change
  useEffect(() => {
    loadBadgeCounts();
  }, [loadBadgeCounts]);

  // Refresh badge counts (can be called externally)
  const refreshBadges = useCallback(() => {
    loadBadgeCounts();
  }, [loadBadgeCounts]);

  return {
    badges,
    loading,
    error,
    refreshBadges
  };
}